import React, { useEffect, useState } from 'react';
import { Box, Typography, Tabs, Tab, styled, useMediaQuery, useTheme, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BackWithBreadcrumbAppbar from '../components/BackWithBreadcrumbAppbar';
import AgendaUI, { GET_SAVED_AGENDA } from '../components/AgendaUi';
import { useQuery } from '@apollo/client';
import LoadingPage from './LoadingPage';

const StyledTabs = styled(Tabs)(({ theme }) => ({
    backgroundColor: '#fff',
    borderRadius: '30px',
    padding: '5px',
    margin: '20px auto',
    width: 'fit-content',
    maxWidth: '95%',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflowX: 'auto',
    '& .MuiTabs-indicator': { display: 'none' },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        borderRadius: '8px',
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    borderRadius: '25px',
    margin: '0 5px',
    minWidth: '160px',
    maxWidth: '320px',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
        minWidth: '120px',
        fontSize: '0.875rem',
        padding: '6px 12px',
    },
    '&.Mui-selected': {
        backgroundColor: '#2D4356',
        color: '#fff',
    },
}));

const DayTabsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    margin: '20px 0',
    [theme.breakpoints.down('sm')]: {
        gap: '4px',
        padding: '0 8px',
    },
}));

const DayTab = styled(Button)(({ theme, selected }) => ({
    borderRadius: '20px',
    padding: '8px 16px',
    minWidth: '100px',
    textTransform: 'none',
    backgroundColor: selected ? '#1aa98b' : '#f0f0f0',
    color: selected ? '#fff' : '#333',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: selected ? '#1aa98b' : '#e0e0e0',
    },
    [theme.breakpoints.down('sm')]: {
        minWidth: '80px',
        padding: '6px 12px',
        fontSize: '0.8rem',
    },
}));

const MyAgenda = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [groupedAgendas, setGroupedAgendas] = useState([]);
    const [currentAgenda, setCurrentAgenda] = useState(0);
    const [currentDay, setCurrentDay] = useState(0);
    const { data, loading, error } = useQuery(GET_SAVED_AGENDA);


    useEffect(() => {
        if (data?.getSavedAgenda) {
            const parsedAgenda = typeof data.getSavedAgenda === 'string'
                ? JSON.parse(data.getSavedAgenda)
                : data.getSavedAgenda;

            const agendaMap = parsedAgenda.reduce((acc, item) => {
                const session = item.sessionDetail;
                const agendaName = item.agendaName;

                if (!acc[agendaName]) {
                    acc[agendaName] = {
                        agendaName,
                        days: {},
                        location: item.location
                    };
                }

                const dayKey = `day-${item.day}`;
                if (!acc[agendaName].days[dayKey]) {
                    acc[agendaName].days[dayKey] = {
                        day: item.day,
                        date: item.date,
                        sessions: []
                    };
                }

                acc[agendaName].days[dayKey].sessions.push({
                    ...session,
                    agendaName: item.agendaName,
                    day: item.day,
                    date: item.date,
                    location: item.location
                });

                return acc;
            }, {});

            const processed = Object.values(agendaMap).map(agenda => ({
                ...agenda,
                days: Object.values(agenda.days)
                    .sort((a, b) => a.day - b.day)
                    .map(day => ({
                        ...day,
                        sessions: day.sessions.sort((a, b) => {
                            const timeToMinutes = timeStr => {
                                const [start] = timeStr.split(' - ');
                                const [hours, minutes] = start.split(':');
                                return parseInt(hours) * 60 + parseInt(minutes);
                            };
                            return timeToMinutes(a.session_time) - timeToMinutes(b.session_time);
                        })
                    }))
            }));

            setGroupedAgendas(processed);
        }
    }, [data]);

    const handleAgendaChange = (event, newValue) => {
        setCurrentAgenda(newValue);
        setCurrentDay(0); // Reset to first day when changing agenda
    };

    const handleDayChange = (newValue) => {
        setCurrentDay(newValue);
    };

    return (
        <>
            <BackWithBreadcrumbAppbar
                links={[{ label: t('dashboard'), href: '/' }]}
                currentPage={t('MyAgenda')}
            />
            {
                loading ? <LoadingPage /> :
                    (<Box sx={{ pt: 4 }}>
                        <Typography variant="h3" align="center" sx={{ mb: 2, fontSize: isMobile ? '1.5rem' : '2rem' }}>
                            {t('My Agenda')}
                        </Typography>

                        {groupedAgendas.length > 0 ? (
                            <>
                                <StyledTabs
                                    value={currentAgenda}
                                    onChange={handleAgendaChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    {groupedAgendas.map((agenda, index) => (
                                        <StyledTab
                                            key={index}
                                            label={agenda.agendaName}
                                        />
                                    ))}
                                </StyledTabs>

                                {groupedAgendas[currentAgenda]?.days?.length > 0 && (
                                    <DayTabsContainer>
                                        {groupedAgendas[currentAgenda].days.map((day, index) => (
                                            <DayTab
                                                key={index}
                                                selected={currentDay === index}
                                                onClick={() => handleDayChange(index)}
                                            >
                                                {t('Day')} {day.day}
                                            </DayTab>
                                        ))}
                                    </DayTabsContainer>
                                )}

                                {groupedAgendas[currentAgenda]?.days[currentDay] && (
                                    <AgendaUI
                                        data={[{
                                            ...groupedAgendas[currentAgenda],
                                            sessionDetail: groupedAgendas[currentAgenda].days[currentDay].sessions,
                                        }]}
                                        showDateHeader={false}
                                    />
                                )}
                            </>
                        ) : (
                            <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                                {t('No saved agenda items found')}
                            </Typography>
                        )}
                    </Box>)}
        </>
    );
};

export default MyAgenda;